import { useEffect, useLayoutEffect, useRef, useCallback, useState } from 'react';
import { NavLink } from 'react-router-dom';
import PaginationControls from '../shared/PaginationControls';
import SearchBar from '../shared/SearchBar';
import useSessionStorageState from '../shared/useSessionStorageState';
import useHomepageFeature from './useHomepageFeature';
import useImagesData from './useImagesData';
import { buildImageAlt, handleThumbnailError, isVideoImage } from '../../utils/imageMedia';
import Loading from '../loading/Loading';
import './images.scss';
import { applySeo, siteName, siteUrl, truncateText } from '../../utils/seo';

const IMAGES_PER_PAGE = 25;
const IMAGES_SEARCH_QUERY_KEY = 'imagesSearchQuery';
const IMAGES_PAGE_STORAGE_KEY = 'imagesPage';
const IMAGES_SCROLL_STORAGE_KEY = 'imagesScroll';
const RESTORE_IMAGES_SCROLL_KEY = 'restoreImagesScroll';
const FEATURED_IMAGE_ORIENTATION_CACHE_KEY = 'featuredImageOrientationCache';
const SEARCH_SCROLL_OFFSET = 104;
const normalizeSearchValue = (value) => value.toLowerCase().trim();
const getCachedFeaturedImageOrientation = (cacheKey) => {
    if (typeof window === 'undefined' || !cacheKey) {
        return '';
    }

    try {
        const cachedValue = localStorage.getItem(FEATURED_IMAGE_ORIENTATION_CACHE_KEY);

        if (!cachedValue) {
            return '';
        }

        const parsedCache = JSON.parse(cachedValue);
        return parsedCache?.[cacheKey] || '';
    } catch (error) {
        return '';
    }
};

const cacheFeaturedImageOrientation = (cacheKey, orientation) => {
    if (typeof window === 'undefined' || !cacheKey || !orientation) {
        return;
    }

    try {
        const cachedValue = localStorage.getItem(FEATURED_IMAGE_ORIENTATION_CACHE_KEY);
        const parsedCache = cachedValue ? JSON.parse(cachedValue) : {};

        parsedCache[cacheKey] = orientation;
        localStorage.setItem(FEATURED_IMAGE_ORIENTATION_CACHE_KEY, JSON.stringify(parsedCache));
    } catch (error) {
        // Ignore storage errors and keep the resolved orientation in memory.
    }
};

const appendHeadLink = (attributes) => {
    if (typeof document === 'undefined') {
        return;
    }

    const selector = Object.entries(attributes)
        .map(([key, value]) => `[${key}="${value}"]`)
        .join('');

    if (document.head.querySelector(`link${selector}`)) {
        return;
    }

    const link = document.createElement('link');

    Object.entries(attributes).forEach(([key, value]) => {
        link.setAttribute(key, value);
    });

    document.head.appendChild(link);
};

const primeOriginConnection = (urlString) => {
    if (!urlString) {
        return;
    }

    try {
        const origin = new URL(urlString).origin;

        appendHeadLink({ rel: 'preconnect', href: origin, crossorigin: 'anonymous' });
        appendHeadLink({ rel: 'dns-prefetch', href: origin });
    } catch (error) {
        // Ignore malformed or relative URLs.
    }
};

const preloadImageUrl = (urlString) => {
    if (!urlString) {
        return;
    }

    appendHeadLink({
        rel: 'preload',
        as: 'image',
        href: urlString
    });
};

const withVersionParam = (urlString, version) => {
    if (!urlString || !version) {
        return urlString;
    }

    const separator = urlString.includes('?') ? '&' : '?';
    return `${urlString}${separator}v=${encodeURIComponent(version)}`;
};

const getPublishedTime = (image) => {
    const publishedTime = new Date(image.published || 0).getTime();
    return Number.isNaN(publishedTime) ? 0 : publishedTime;
};

const toNavigationImage = (image) => ({
    id: String(image.id),
    title: image.title,
    url_regular: image.url_regular,
    url_hd: image.url_hd,
    url_regular_fallback: image.url_regular_fallback,
    url_hd_fallback: image.url_hd_fallback,
    video_file: image.video_file,
    encoded_video_file: image.encoded_video_file,
    loop_video: image.loop_video
});

const getNavigationType = () => {
    if (typeof performance === 'undefined' || !performance.getEntriesByType) {
        return '';
    }

    const [navigationEntry] = performance.getEntriesByType('navigation');

    return navigationEntry?.type || '';
};

const hasImageDetailReferrer = () => {
    if (typeof document === 'undefined' || typeof window === 'undefined' || !document.referrer) {
        return false;
    }

    try {
        const referrerUrl = new URL(document.referrer);

        return referrerUrl.origin === window.location.origin
            && /^\/images\/[^/]+\/?$/.test(referrerUrl.pathname);
    } catch (error) {
        return false;
    }
};

const shouldRestoreSavedGridScroll = () => {
    try {
        return sessionStorage.getItem(RESTORE_IMAGES_SCROLL_KEY) === 'true'
            && (getNavigationType() === 'back_forward' || hasImageDetailReferrer());
    } catch (error) {
        return false;
    }
};

const Images = () => {
    const [searchQuery, setSearchQuery] = useSessionStorageState(IMAGES_SEARCH_QUERY_KEY, '');
    const { homepageFeature, homepageFeatureLoading } = useHomepageFeature();
    const API_URL = process.env.REACT_APP_API_URL;
    const { images, imagesLoading, imagesError } = useImagesData(API_URL);
    const [featuredImageOrientation, setFeaturedImageOrientation] = useState('');
    const searchShellRef = useRef(null);
    const shouldRestoreSearchRef = useRef(false);
    const shouldRestoreGridScrollRef = useRef(shouldRestoreSavedGridScroll());
    const previousSearchQueryRef = useRef(searchQuery);

    const [currentPage, setCurrentPage] = useSessionStorageState(IMAGES_PAGE_STORAGE_KEY, 1);

    const restoreSavedGridScroll = useCallback(() => {
        const savedScroll = sessionStorage.getItem(IMAGES_SCROLL_STORAGE_KEY);

        if (!savedScroll) {
            return false;
        }

        const scrollY = parseInt(savedScroll, 10);

        if (Number.isNaN(scrollY)) {
            return false;
        }

        window.scrollTo({ top: scrollY, behavior: 'auto' });
        sessionStorage.removeItem(RESTORE_IMAGES_SCROLL_KEY);
        shouldRestoreGridScrollRef.current = false;

        return true;
    }, []);

    useEffect(() => {
        primeOriginConnection(API_URL);
    }, [API_URL]);

    useEffect(() => {
        if (previousSearchQueryRef.current === searchQuery) {
            return;
        }

        previousSearchQueryRef.current = searchQuery;
        setCurrentPage(1);
        sessionStorage.removeItem(IMAGES_SCROLL_STORAGE_KEY);
        sessionStorage.removeItem(RESTORE_IMAGES_SCROLL_KEY);
    }, [searchQuery, setCurrentPage]);

    useLayoutEffect(() => {
        if (shouldRestoreGridScrollRef.current) {
            return;
        }

        sessionStorage.removeItem(IMAGES_SCROLL_STORAGE_KEY);
        sessionStorage.removeItem(RESTORE_IMAGES_SCROLL_KEY);
        sessionStorage.removeItem(IMAGES_PAGE_STORAGE_KEY);
        setCurrentPage(1);
        window.scrollTo(0, 0);
    }, [setCurrentPage]);

    useEffect(() => {
        const restoreAfterMobileBack = (event) => {
            const shouldRestore = sessionStorage.getItem(RESTORE_IMAGES_SCROLL_KEY) === 'true'
                && (event.persisted || shouldRestoreGridScrollRef.current || getNavigationType() === 'back_forward');

            if (!shouldRestore) {
                return;
            }

            shouldRestoreGridScrollRef.current = true;
            restoreSavedGridScroll();

            requestAnimationFrame(() => {
                restoreSavedGridScroll();
            });
        };

        window.addEventListener('pageshow', restoreAfterMobileBack);

        return () => {
            window.removeEventListener('pageshow', restoreAfterMobileBack);
        };
    }, [restoreSavedGridScroll]);

    useLayoutEffect(() => {
        if (!shouldRestoreSearchRef.current || !searchShellRef.current || searchQuery.trim()) {
            return;
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!searchShellRef.current) {
                    return;
                }

                const absoluteTop = window.scrollY + searchShellRef.current.getBoundingClientRect().top;
                const targetTop = Math.max(0, absoluteTop - SEARCH_SCROLL_OFFSET);
                window.scrollTo({ top: targetTop });
            });
        });

        shouldRestoreSearchRef.current = false;
    }, [homepageFeatureLoading, searchQuery]);

    useLayoutEffect(() => {
        if (!shouldRestoreGridScrollRef.current) {
            return;
        }

        if (images.length === 0) return;

        restoreSavedGridScroll();
    }, [currentPage, images.length, restoreSavedGridScroll]);

    const normalizedSearchQuery = normalizeSearchValue(searchQuery);
    const matchesSearch = (image) => {
        if (!normalizedSearchQuery) {
            return true;
        }

        return normalizeSearchValue(image.title || '').includes(normalizedSearchQuery);
    };

    const sortedImages = [...images].sort((a, b) => getPublishedTime(b) - getPublishedTime(a));
    const filteredImages = sortedImages.filter(matchesSearch);
    const featuredImageFromGallery = homepageFeature?.image_id
        ? sortedImages.find((image) => String(image.id) === String(homepageFeature.image_id))
        : null;
    const shouldShowFeaturedImage = !normalizedSearchQuery && !homepageFeatureLoading;
    const featuredImage = shouldShowFeaturedImage
        ? homepageFeature?.featured_image || featuredImageFromGallery || null
        : null;
    const featuredImageUrl = withVersionParam(
        featuredImage?.url_regular,
        homepageFeature?.featured_image_updated_at
    );
    const featuredImageOrientationCacheKey = `${featuredImage?.id || 'featured'}:${featuredImageUrl || ''}`;
    const featuredImageId = featuredImage?.id;
    const filteredImagesForGrid = normalizedSearchQuery
        ? filteredImages
        : filteredImages.filter((image) => image.id !== featuredImageId);
    const searchableImages = normalizedSearchQuery ? filteredImages : [];
    const shouldReserveFeaturedSlot = !normalizedSearchQuery;

    const totalImageCount = normalizedSearchQuery
        ? searchableImages.length
        : filteredImagesForGrid.length;

    const parsedCurrentPage = Number.parseInt(currentPage, 10);
    const normalizedCurrentPage = Number.isNaN(parsedCurrentPage) || parsedCurrentPage < 1
        ? 1
        : parsedCurrentPage;
    const totalPages = Math.max(1, Math.ceil(totalImageCount / IMAGES_PER_PAGE));
    const safeCurrentPage = Math.min(normalizedCurrentPage, totalPages);

    const startIndex = (safeCurrentPage - 1) * IMAGES_PER_PAGE;
    const endIndex = startIndex + IMAGES_PER_PAGE;

    const pagedImages = filteredImagesForGrid.slice(startIndex, endIndex);

    const displayedImages = normalizedSearchQuery
        ? searchableImages.slice(startIndex, endIndex)
        : pagedImages;
    const navigationImages = normalizedSearchQuery
        ? searchableImages.map(toNavigationImage)
        : featuredImage
            ? [
                toNavigationImage(featuredImage),
                ...filteredImagesForGrid.map(toNavigationImage)
            ]
            : filteredImagesForGrid.map(toNavigationImage);

    const createImageRouteState = (image) => ({
        image,
        navigationImages,
        searchQuery: searchQuery.trim()
    });

    const saveGalleryNavigationState = (event, image) => {
        shouldRestoreGridScrollRef.current = true;
        sessionStorage.setItem(IMAGES_SCROLL_STORAGE_KEY, window.scrollY);
        sessionStorage.setItem(RESTORE_IMAGES_SCROLL_KEY, 'true');
        sessionStorage.setItem(IMAGES_PAGE_STORAGE_KEY, String(safeCurrentPage));
        sessionStorage.setItem('selectedImage', JSON.stringify(createImageRouteState(image)));

    };

    const updateSearchQuery = (value) => {
        shouldRestoreSearchRef.current = value === '' && searchQuery.trim() !== '';
        setSearchQuery(value);
    };

    useEffect(() => {
        const pageTitle = normalizedSearchQuery
            ? `Search Astrophotography Images for "${searchQuery.trim()}" | AstroDavido`
            : 'AstroDavido | David Joyce Astrophotography';
        const pageDescription = normalizedSearchQuery
            ? truncateText(`Search astrophotography images on AstroDavido for ${searchQuery.trim()}, including nebula, galaxy, and planetary targets captured by David Joyce.`, 155)
            : 'AstroDavido is the astrophotography portfolio of David Joyce in Lexington, Kentucky, featuring deep-sky images, narrowband nebulae, galaxies, and planetary imaging.';
        const listPreviewImages = sortedImages.slice(0, 3).map((image) => ({
            '@type': 'ImageObject',
            name: image.title,
            contentUrl: image.url_regular,
            url: `${siteUrl}/images/${image.id}/`
        }));

        applySeo({
            title: pageTitle,
            description: pageDescription,
            canonicalPath: '/',
            ogImage: featuredImageUrl || sortedImages[0]?.url_regular || `${siteUrl}/logo192.png`,
            structuredData: {
                '@context': 'https://schema.org',
                '@type': 'CollectionPage',
                name: `${siteName} Images`,
                description: pageDescription,
                url: `${siteUrl}/`,
                hasPart: listPreviewImages
            }
        });
    }, [featuredImage, featuredImageUrl, normalizedSearchQuery, searchQuery, sortedImages]);

    useEffect(() => {
        if (!featuredImageUrl) return;

        primeOriginConnection(featuredImageUrl);
        preloadImageUrl(featuredImageUrl);
    }, [featuredImageUrl]);

    useEffect(() => {
        if (!featuredImageUrl) {
            setFeaturedImageOrientation('');
            return undefined;
        }

        const cachedOrientation = getCachedFeaturedImageOrientation(featuredImageOrientationCacheKey);

        if (cachedOrientation) {
            setFeaturedImageOrientation(cachedOrientation);
            return undefined;
        }

        let isCancelled = false;
        const probeImage = new window.Image();

        setFeaturedImageOrientation('');

        probeImage.onload = () => {
            if (isCancelled) {
                return;
            }

            const orientation = probeImage.naturalHeight > probeImage.naturalWidth ? 'portrait' : 'landscape';

            setFeaturedImageOrientation(orientation);
            cacheFeaturedImageOrientation(featuredImageOrientationCacheKey, orientation);
        };

        probeImage.onerror = () => {
            if (isCancelled) {
                return;
            }

            setFeaturedImageOrientation('landscape');
        };

        probeImage.src = featuredImageUrl;

        return () => {
            isCancelled = true;
            probeImage.onload = null;
            probeImage.onerror = null;
            probeImage.src = '';
        };
    }, [featuredImageOrientationCacheKey, featuredImageUrl]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages || page === safeCurrentPage) return;

        sessionStorage.removeItem(IMAGES_SCROLL_STORAGE_KEY);
        sessionStorage.removeItem(RESTORE_IMAGES_SCROLL_KEY);
        setCurrentPage(page);

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!searchShellRef.current) {
                    return;
                }

                const absoluteTop = window.scrollY + searchShellRef.current.getBoundingClientRect().top;
                const targetTop = Math.max(0, absoluteTop - SEARCH_SCROLL_OFFSET);
                window.scrollTo({ top: targetTop, behavior: 'auto' });
            });
        });
    };

    if (imagesLoading && images.length === 0) {
        return (
            <Loading
                fullScreen
            />
        );
    }

    return (
        <>
            {shouldReserveFeaturedSlot ? (
                <div className="featured-image-shell">
                    {featuredImage ? (
                        <NavLink
                            className={`featured-image-card ${featuredImageOrientation === 'portrait' ? 'featured-image-card--portrait' : ''} ${featuredImageOrientation ? 'featured-image-card--ready' : 'featured-image-card--resolving'}`}
                            to={`/images/${featuredImage.id}/`}
                            state={createImageRouteState(featuredImage)}
                            onClick={(event) => saveGalleryNavigationState(event, featuredImage)}
                            reloadDocument
                        >
                            <div className="featured-image-media">
                                <img
                                    src={featuredImageUrl}
                                    alt={buildImageAlt(featuredImage.title)}
                                    loading="eager"
                                    fetchpriority="high"
                                    onError={(event) => handleThumbnailError(event, featuredImage.url_regular_fallback)}
                                />
                                {isVideoImage(featuredImage) ? (
                                    <span className="image-video-badge image-video-badge--featured" role="img" aria-label="Video">
                                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                            <path d="M8 5.5v13l11-6.5-11-6.5z" fill="currentColor" />
                                        </svg>
                                    </span>
                                ) : null}
                            </div>

                            <div className="featured-image-copy">
                                <span className="featured-image-kicker">
                                    AstroDavido Featured Image
                                </span>
                                <h1>{featuredImage.title}</h1>
                                <p className="featured-image-cta">View full image</p>
                            </div>
                        </NavLink>
                    ) : (
                        <div
                            className="featured-image-card featured-image-card--placeholder"
                            aria-hidden="true"
                        >
                            <div className="featured-image-media featured-image-media--placeholder" />
                            <div className="featured-image-copy featured-image-copy--placeholder">
                                <span className="featured-image-kicker featured-image-kicker--placeholder" />
                                <span className="featured-image-line featured-image-line--title" />
                                <span className="featured-image-line featured-image-line--subtitle" />
                            </div>
                        </div>
                    )}
                </div>
            ) : null}

            <div ref={searchShellRef}>
                <SearchBar
                    id="images-search"
                    label="Search Images"
                    placeholder="Search by title or target..."
                    value={searchQuery}
                    onChange={updateSearchQuery}
                    onClear={() => updateSearchQuery('')}
                    meta={normalizedSearchQuery
                        ? `${searchableImages.length} result${searchableImages.length === 1 ? '' : 's'} for "${searchQuery.trim()}"`
                        : null}
                />
            </div>

            <PaginationControls
                currentPage={safeCurrentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />

            {imagesLoading ? null : displayedImages.length ? (
                <div className="images-grid-container">
                    {displayedImages.map((image) => {
                        const dateString = new Date(image.published).toDateString();

                        return (
                            <div key={image.id} className="image-col">
                                <NavLink
                                    className="image-link"
                                    to={`/images/${image.id}/`}
                                    state={createImageRouteState(image)}
                                    onClick={(event) => saveGalleryNavigationState(event, image)}
                                    reloadDocument
                                >
                                    <figure className="grid-item grow">
                                        <div className="grid-item-media">
                                            <img
                                                src={image.url_regular}
                                                alt={buildImageAlt(image.title)}
                                                loading="lazy"
                                                onError={(event) => handleThumbnailError(event, image.url_regular_fallback)}
                                            />
                                            {isVideoImage(image) ? (
                                                <span className="image-video-badge" role="img" aria-label="Video">
                                                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                                        <path d="M8 5.5v13l11-6.5-11-6.5z" fill="currentColor" />
                                                    </svg>
                                                </span>
                                            ) : null}
                                        </div>
                                        <p className="image-title">{image.title}</p>
                                        <p className="image-date">{dateString}</p>
                                    </figure>
                                </NavLink>
                            </div>
                        );
                    })}
                </div>
            ) : imagesError ? (
                <p className="images-empty-state images-empty-state--error">
                    {imagesError}
                </p>
            ) : (
                <p className="images-empty-state">
                    No images matched "{searchQuery.trim()}". Try another object name, target, or keyword.
                </p>
            )}

            <PaginationControls
                currentPage={safeCurrentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />
        </>
    );
};

export default Images;
